<template>
    <div @click.stop="toggle_instructors_and_groups()" class="title ig-title" role="button" tabindex="0"
         :aria-expanded="data.show" @keydown.enter.prevent="toggle_instructors_and_groups()">
        Instructors & Groups
        <span class="material-symbols-outlined ig-title-chevron" :class="{open:data.show}" aria-hidden="true">expand_more</span>
    </div>


    <section v-show="data.show" class="ig">

        <div class="ig-toolbar">
            <span class="ig-count" v-if="data.instructors">
                {{ active_count }} active · {{ data.instructors.length - active_count }} inactive
            </span>
            <button class="ig-add" type="button" title="Create a new, blank instructor at the top of the list"
                    @click.stop="create_instructor()">
                <span class="material-symbols-outlined" aria-hidden="true">person_add</span>
                Add instructor
            </button>
        </div>

        <!-- Rows, not cards: one row per instructor. Opening a row shows that teacher's groups
             in a table right under it, one row per student; a group's own cells (subject,
             schedule, hours, its buttons) span all of its students' rows. -->
        <table class="ig-table ig-instructors">
            <thead>
                <tr>
                    <th scope="col" class="ig-c-name">Instructor</th>
                    <th scope="col" class="ig-c-status">Status</th>
                    <th scope="col" class="ig-c-groups">Groups</th>
                    <th scope="col" class="ig-c-actions"><span class="ig-sr">Actions</span></th>
                </tr>
            </thead>

            <tbody v-for="instructor in data.instructors" :key="instructor.id" class="ig-instructor"
                   :class="{'ig-off':!instructor.ordering, 'ig-open':instructor.show_groups}">

                <tr class="ig-row" @click="click_row(instructor)">
                    <td class="ig-c-name">
                        <button class="ig-expand" type="button" :aria-expanded="!!instructor.show_groups"
                                :title="instructor.show_groups ? 'Hide groups' : 'Show groups'"
                                @click.stop="toggle_groups(instructor)">
                            <span class="material-symbols-outlined ig-chevron" :class="{loading:instructor.loading_groups}"
                                  aria-hidden="true">{{ instructor.loading_groups ? 'progress_activity' : 'chevron_right' }}</span>
                            <span class="ig-name">{{ instructor.ui }}</span>
                        </button>
                    </td>
                    <td class="ig-c-status">
                        <span class="ig-status" :class="{off:!instructor.ordering}">{{ instructor.ordering ? 'Active' : 'Inactive' }}</span>
                    </td>
                    <td class="ig-c-groups">
                        {{ group_total(instructor) }}<span class="ig-unit">{{ group_total(instructor)==1 ? ' group' : ' groups' }}</span>
                    </td>
                    <td class="ig-c-actions" @click.stop>
                        <div class="ig-actions">
                            <IconButton icon="space_dashboard" label="Dashboard"
                                :tip="`Open ${instructor.ui}'s dashboard in a new tab`"
                                @click.stop="send_to_instructor_dashboard(instructor.user.id)"/>
                            <IconButton icon="receipt_long" label="Hours"
                                :tip="`Open ${instructor.ui}'s hoursheet in a new tab`"
                                @click.stop="send_to_hoursheet(instructor.user.id)"/>
                            <IconButton icon="edit" label="Edit"
                                :tip="`Edit ${instructor.ui}'s details`"
                                @click.stop="show_edit_window(instructor,'instructor')"/>
                            <IconButton icon="forward_to_inbox" label="Send hrs"
                                :tip="`Send ${instructor.ui} their hoursheet by WhatsApp (asks first)`"
                                @click.stop="send_hoursheet(instructor)"/>
                            <IconButton icon="key" label="Login"
                                :tip="`Set a new password for ${instructor.ui} and send their login by WhatsApp`"
                                @click.stop="send_credentials(instructor)"/>
                            <IconButton icon="person_off" label="Inactivate" danger
                                :tip="`Inactivate ${instructor.ui} (asks first)`"
                                @click.stop="remove_instructor(instructor)"/>
                        </div>
                    </td>
                </tr>

                <tr v-if="instructor.show_groups" class="ig-detail">
                    <td colspan="4">
                        <div class="ig-groups">

                            <div class="ig-groups-toolbar">
                                <button class="ig-add ig-add-quiet" type="button"
                                        :title="`Create a new, empty group for ${instructor.ui}`"
                                        @click.stop="create_group(instructor)">
                                    <span class="material-symbols-outlined" aria-hidden="true">add</span>
                                    New group
                                </button>
                                <button class="ig-add ig-add-quiet" type="button"
                                        :title="`Choose existing groups to assign to ${instructor.ui}`"
                                        @click.stop="show_addfromlist_window(instructor,'Group')">
                                    <span class="material-symbols-outlined" aria-hidden="true">playlist_add</span>
                                    Assign existing group
                                </button>
                            </div>

                            <p v-if="!group_total(instructor)" class="ig-empty">No groups yet.</p>

                            <table v-else class="ig-table ig-gtable">
                                <thead>
                                    <tr>
                                        <th scope="col" class="ig-g-name">Group</th>
                                        <th scope="col" class="ig-g-schedule">Schedule</th>
                                        <th scope="col" class="ig-g-hours">Hours</th>
                                        <th scope="col" class="ig-g-actions"><span class="ig-sr">Group actions</span></th>
                                        <th scope="col" class="ig-s-name">Student</th>
                                        <th scope="col" class="ig-s-level">Level</th>
                                        <th scope="col" class="ig-s-actions"><span class="ig-sr">Student actions</span></th>
                                    </tr>
                                </thead>

                                <tbody v-for="group in instructor.groups" :key="group.id" class="ig-group">
                                    <tr v-for="(student,index) in group_rows(group)" :key="student ? student.id : 'none'"
                                        :class="{'ig-lead':index==0}">

                                        <template v-if="index==0">
                                            <td class="ig-g ig-g-name" :rowspan="group_rows(group).length">
                                                <span class="ig-subject" :class="{none:!group.subject}">{{ group.subject || 'No subject' }}</span>
                                                <span v-if="group.comments" class="ig-comments">{{ group.comments }}</span>
                                            </td>
                                            <td class="ig-g ig-g-schedule" :rowspan="group_rows(group).length" data-label="Schedule">
                                                <template v-if="schedule_entries(group.schedule).length">
                                                    <span v-for="(entry,i) in schedule_entries(group.schedule)" :key="i"
                                                          class="ig-schedule-entry">{{ entry }}</span>
                                                </template>
                                                <span v-else class="ig-none">—</span>
                                            </td>
                                            <td class="ig-g ig-g-hours" :rowspan="group_rows(group).length" data-label="Hours">{{ hours(group.duration) }}</td>
                                            <td class="ig-g ig-g-actions" :rowspan="group_rows(group).length">
                                                <div class="ig-actions">
                                                    <IconButton icon="group_add" label="Add"
                                                        tip="Add existing students to this group"
                                                        @click.stop="show_addfromlist_window(group,'Student')"/>
                                                    <IconButton icon="person_add" label="New"
                                                        tip="Create a new student in this group"
                                                        @click.stop="create_student(group)"/>
                                                    <IconButton icon="edit" label="Edit"
                                                        tip="Edit this group"
                                                        @click.stop="show_edit_window(group,'group')"/>
                                                    <IconButton icon="campaign" label="Offer"
                                                        tip="Offer this group to other teachers by WhatsApp"
                                                        @click.stop="show_offer_group(group)"/>
                                                    <IconButton icon="link_off" label="Remove" danger
                                                        :tip="`Remove this group from ${instructor.ui} (asks first)`"
                                                        @click.stop="remove_group(instructor,group)"/>
                                                </div>
                                            </td>
                                        </template>

                                        <template v-if="student">
                                            <td class="ig-s ig-s-name">
                                                <span class="material-symbols-outlined" aria-hidden="true">person</span>
                                                <span>{{ student.ui }}</span>
                                            </td>
                                            <td class="ig-s ig-s-level" :class="{none:!student.level}"><span class="ig-level">{{ student.level || '—' }}</span></td>
                                            <td class="ig-s ig-s-actions">
                                                <div class="ig-actions">
                                                    <IconButton icon="space_dashboard" label="Dashboard"
                                                        :tip="`Open ${student.ui}'s dashboard in a new tab`"
                                                        @click.stop="send_to_student_dashboard(student.user.id)"/>
                                                    <IconButton icon="payments" label="Payments"
                                                        :tip="`See and edit ${student.ui}'s payments`"
                                                        @click.stop="show_payments_window(group,student)"/>
                                                    <IconButton icon="edit" label="Edit"
                                                        :tip="`Edit ${student.ui}'s details`"
                                                        @click.stop="show_edit_window(student,'student')"/>
                                                    <IconButton icon="key" label="Login"
                                                        :tip="`Set a new password for ${student.ui} and send their login by WhatsApp`"
                                                        @click.stop="send_credentials(student)"/>
                                                    <IconButton icon="person_remove" label="Remove" danger
                                                        :tip="`Remove ${student.ui} from this group (asks first)`"
                                                        @click.stop="remove_student(group,student)"/>
                                                </div>
                                            </td>
                                        </template>
                                        <td v-else class="ig-s ig-s-none" colspan="3">No students in this group.</td>

                                    </tr>
                                </tbody>
                            </table>

                        </div>
                    </td>
                </tr>

            </tbody>
        </table>

    </section>

    <EditInstructor v-if="data.edit_instructor" :instructor="data.edit_instructor" @close="data.edit_instructor=null"/>
    <EditStudent v-if="data.edit_student" :student="data.edit_student" @close="data.edit_student=null"/>
    <EditGroup v-if="data.edit_group" :group="data.edit_group" @close="data.edit_group=null"/>
    <AddFromList/>
    <Payments/>
    <OfferGroup/>


</template>


<script setup>
    import {ref,reactive,computed,onMounted,onBeforeMount, provide} from 'vue'
    import axios from 'axios'
    import EditInstructor from '@/components/edit/EditInstructor.vue'
    import EditStudent from '@/components/edit/EditStudent.vue'
    import EditGroup from '@/components/edit/EditGroup.vue'
    import AddFromList from '@/components/AddFromList.vue'
    import Payments from '@/components/Payments.vue'
    import OfferGroup from '@/components/OfferGroup.vue'
    import IconButton from '@/components/IconButton.vue'

    import {useStore} from 'vuex'
    import {ask,ask_whatsapp} from '@/confirm'

    let store=useStore()

    let data=reactive({
        edit_instructor:null,
        edit_student:null,
        edit_group:null,
        children:{},
        parent:{},
        selected:{},
        payments_student:{},
        student_payments:{},
        show:false,
    })
    provide('data',data)

    let offer_data=reactive({
        show:false,
        hide:true,
        selected:[],
        instructors:[],
        group:null,
    })
    provide('offer_data',offer_data)

    onBeforeMount(async()=>{

        let response=await axios.post('/admin/get_instructors/')
        data.instructors=response.data
    })


    async function create_instructor(){
        let response=await axios.post('/admin/create_instructor/')
        let new_instructor=response.data
        data.instructors.unshift(new_instructor)

    }

    async function remove_instructor(instructor){

        let confirm_delete=await ask({
            title:`Inactivate ${instructor.ui}?`,
            message:'Their account is deactivated, so they can no longer log in.',
            confirm_label:'Inactivate', danger:true, icon:'person_off',
        })
        if (!confirm_delete){
            return
        }

        let response=await axios.post('/admin/remove_instructor/',{instructor_id:instructor.id})

        for (let instructr of data.instructors){
            if(instructr.id==instructor.id){
                const index=Object.values(data.instructors).indexOf(instructr)
                data.instructors.splice(index,1)
                break

            }
        }

    }


    async function create_group(instructor){
        let response=await axios.post('/admin/create_group/',{instructor_id:instructor.id})
        // instructor.groups.push(response.data)
        console.log(response)
        let new_group=response.data
        instructor.groups.unshift(new_group)
        // instructor.groups = Object.fromEntries(Object.entries(instructor.groups).sort((a,b)=>a[1].student_ordering-b[1].student_ordering));
    }



    async function remove_group(instructor,group){

        let confirm_delete=await ask({
            title:`Remove this group from ${instructor.ui}?`,
            message:`${[group.subject, group.schedule].filter(Boolean).join(' · ') || group.ui}\n\nThe group itself is kept; it is only unassigned from this teacher.`,
            confirm_label:'Remove', danger:true, icon:'link_off',
        })
        if (!confirm_delete){
            return
        }



        let response=await axios.post('/admin/remove_group/',{instructor_id:instructor.id,group_id:group.id})
        console.log(response)


        for(const group_obj of instructor.groups){
            if(group_obj.id==group.id){
                const index=Object.values(instructor.groups).indexOf(group_obj)
                instructor.groups.splice(index,1)
                break
            }
        }


    }


    async function create_student(group){
        let response=await axios.post('/admin/create_student/',{group_id:group.id})
        let new_student=response.data
        group.student.push(new_student)
        // console.log(group.student)

    }

    async function remove_student(group,student){
        let confirm_delete=await ask({
            title:`Remove ${student.ui} from this group?`,
            message:'They leave this group.',
            warning:'If this is their only group, their account is also deactivated and they can no longer log in.',
            confirm_label:'Remove', danger:true, icon:'person_remove',
        })
        if (!confirm_delete){
            return
        }


        let response=await axios.post('/admin/remove_student/',{group_id:group.id,student_id:student.id})
        console.log(student)

        for(let current_student of group.student){
            if(current_student.id==student.id){
                let index=group.student.indexOf(student)
                group.student.splice(index,1)
                break
            }

        }
    }




  // Each kind of row has its own window (components/edit/); `kind` picks it.
  async function show_edit_window(object,kind){

      let response=await axios.post('/admin/get_object/',{object:{endpoint:object.endpoint,id:object.id}})
      Object.assign(object,response.data)
      data[`edit_${kind}`]=object

  }


  async function show_addfromlist_window(parent,child_endpoint){

      let response =  await axios.post('/admin/get_objects/',{endpoint:child_endpoint})
      data.children=response.data

      data.children_endpoint=child_endpoint
      // Object.assign(data.parent,parent)
      data.parent=reactive(parent)
      data.show_addfromlist_window=true
      data.hide_addfromlist_window=false



    let property = child_endpoint.toLowerCase()

    console.log('propertying',property);
    if(data.parent.endpoint=='Instructor'){
        property=`${property}s`
    }
    for (let child of data.children){
        for (let ichild of parent[property]){
            if (ichild.id==child.id){
                child.selected=true
            }
        }
    }


  }



    function toggle_instructors_and_groups(){
        data.show=!data.show
    }



    // A click anywhere on an instructor's row opens it too, not only on the name. Not when
    // the click ends a text selection (copying a name), and the buttons' cell stops its own.
    function click_row(instructor){
        if (window.getSelection && String(window.getSelection()).length){
            return
        }
        toggle_groups(instructor)
    }



    // Opening re-fetches the instructor's groups every time, as before.
    async function toggle_groups(instructor){
        if (instructor.show_groups){
            instructor.show_groups=false
            return
        }
        if (instructor.loading_groups){
            return
        }
        instructor.loading_groups=true
        try{
            let response=await axios.post('/admin/get_groups/',{instructor_id:instructor.id})
            // Replace, not merge: merging left groups removed elsewhere on screen.
            instructor.groups=response.data
            instructor.show_groups=true
        }finally{
            instructor.loading_groups=false
        }
    }


    const active_count=computed(()=>(data.instructors||[]).filter(i=>i.ordering).length)

    function group_total(instructor){
        return instructor.groups ? Object.keys(instructor.groups).length : 0
    }

    // The groups table has a row per student; a group without students still gets one row,
    // for its own cells.
    function group_rows(group){
        return group.student && group.student.length ? group.student : [null]
    }

    // "MO 12:00 13:00 | TH 11:00 12:00" shows each time on its own line. Display only:
    // the text itself is left exactly as typed.
    function schedule_entries(schedule){
        return String(schedule || '').split('|').map(entry=>entry.trim()).filter(Boolean)
    }

    // 1.00 -> 1, 1.50 -> 1.5
    function hours(duration){
        if (duration===null || duration===undefined || duration===''){
            return '—'
        }
        const number=Number(duration)
        return Number.isFinite(number) ? String(number) : String(duration)
    }




    async function show_payments_window(group,student){
        let payments=await axios.post('/payments/get_payments/',{student_id:student.id})
        let instructor_response=await axios.post('/payments/get_plain_instructors/')
        let student_response=await axios.post('/payments/get_plain_students/',{student_id:student.id})
        let group_response=await axios.post('/payments/get_plain_groups/',{student_id:student.id})


        data.emergency_group=group

        data.payment_groups=group_response.data
        data.payment_instructors=instructor_response.data
        data.payment_students=student_response.data
        data.student_payments=payments.data

        data.payments_student=student
        data.show_payments_window=true
        data.hide_payments_window=false

        console.log(data.payment_groups)

    }



    function send_to_student_dashboard(student_id){
        window.open('/student-dashboard?user_id='+student_id, '_blank');

    }

    function send_to_instructor_dashboard(instructor_id){
        window.open('/instructor-dashboard?user_id='+instructor_id, '_blank');

    }

    function send_to_hoursheet(instructor_id){
        window.open('/hoursheet?user_id='+instructor_id,'_blank')
    }

async function send_credentials(person){
    const is_student=person.user && person.user.role=='Student'
    const go=await ask_whatsapp({
        title:`Send login details to ${person.ui}?`,
        to:person,
        message:is_student
            ? 'They get a WhatsApp welcome message with their username, password and the training link.'
            : 'They get a WhatsApp message with their username and password.',
        input:{
            label:'New password',
            placeholder:'Leave blank to use the default password',
            hint:'This password is set on their account and sent to them in the message.',
            secret:true,
        },
        warning:'Their current password stops working once this is sent.',
        confirm_label:'Set & send',
    })
    if(go===false){
        return
    }
    let user=person.user
    let response=await axios.post('/admin/send_credentials/',{user_id:user.id,password:go})
    if (response.data.status){
        alert(`Credentials message status: ${response.data.status}`)
    }
}

async function send_hoursheet(instructor){
    const go=await ask_whatsapp({
        title:`Send the hoursheet to ${instructor.ui}?`,
        to:instructor,
        message:'They get a WhatsApp message with their hoursheet.',
    })
    if(!go){
        return
    }
    let response=await axios.post('/admin/send_hoursheet/',{instructor_id:instructor.id})

        alert(`Hoursheet message status: ${response.data.status}`)

}



async function show_offer_group(group){
        offer_data.show=true
        offer_data.hide=false

        let response=await axios.post('/admin/get_active_instructors/')
        offer_data.instructors=response.data
        offer_data.group=group
        console.log('offer_data',offer_data)
    }




</script>


<style scoped>

    /* ---- section title (keeps the dashboard's big heading, scales down on phones) ---- */
    .ig-title{
        display:flex;
        align-items:center;
        justify-content:center;
        gap:8px;
        cursor:pointer;
        user-select:none;
        font-size:clamp(34px,8vw,65px);
        min-height:auto;
        white-space:normal;
        overflow:visible;
    }
    .ig-title-chevron{
        font-size:0.6em;
        transition:transform 0.2s ease;
    }
    .ig-title-chevron.open{ transform:rotate(180deg); }


    /* ---- palette + font: the same quiet look as the edit windows ---- */
    .ig{
        --ig-bg:#f5f3ef;
        --ig-card:#ffffff;
        --ig-head:#faf8f5;
        --ig-stripe:#faf9f7;
        --ig-line:#e4dfd7;
        --ig-line-soft:#eeeae4;
        --ig-text:#2d2f35;
        --ig-muted:#71747c;
        --ig-accent:#06739e;
        --ig-accent-dark:#055a7c;
        --ig-hover:rgba(6,115,158,0.10);
        --ig-row-hover:rgba(6,115,158,0.05);
        --ig-row-open:rgba(6,115,158,0.08);
        --ig-veil:rgba(6,115,158,0.10);
        --ig-on:#2e8a5d;
        --ig-off:#b0b3b9;

        display:flex;
        flex-direction:column;
        gap:10px;
        width:100%;
        max-width:1400px;
        margin:0 auto;
        padding:12px;
        font-size:14px;
        line-height:20px;
        color:var(--ig-text);
        background:var(--ig-bg);
        border-radius:14px;
    }
    .ig,
    .ig *:not(.material-symbols-outlined){
        font-family:system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;
    }

    .ig-toolbar{
        display:flex;
        flex-wrap:wrap;
        align-items:center;
        justify-content:space-between;
        gap:8px;
    }
    .ig-count{ color:var(--ig-muted); }


    /* ---- text buttons for the "add" actions ---- */
    .ig-add{
        display:inline-flex;
        align-items:center;
        gap:6px;
        height:40px;
        min-height:40px;
        padding:0 14px;
        font-size:14px;
        font-weight:600;
        word-break:normal;
        color:#ffffff;
        background:var(--ig-accent);
        border:none;
        border-radius:10px;
        box-shadow:none;
    }
    .ig-add:hover{
        color:#ffffff;
        background:var(--ig-accent-dark);
        box-shadow:none;
    }
    .ig-add-quiet{
        color:var(--ig-accent);
        background:var(--ig-card);
        border:1px solid var(--ig-line);
    }
    .ig-add-quiet:hover{
        color:var(--ig-accent-dark);
        background:var(--ig-hover);
    }
    .ig-add:focus-visible,
    .ig-expand:focus-visible{
        outline:2px solid var(--ig-accent);
        outline-offset:2px;
    }


    /* ---- tables. App.vue styles every table on the site (centred, a thick border under
       each row, every second row run through a contrast filter); none of that here. ---- */
    .ig-table{
        width:100%;
        border-collapse:separate;
        border-spacing:0;
        text-align:left;
        align-self:stretch;
        justify-self:stretch;
    }
    .ig-table thead,
    .ig-table tr{
        border:none;
        backdrop-filter:none;
    }
    .ig-table th,
    .ig-table td{
        min-width:0;
        padding:0;
        text-align:left;
        vertical-align:middle;
    }
    .ig-table th{
        padding:8px 10px;
        font-size:11px;
        font-weight:600;
        line-height:16px;
        letter-spacing:0.06em;
        text-transform:uppercase;
        white-space:nowrap;
        color:var(--ig-muted);
        background:var(--ig-head);
    }

    .ig-sr{
        position:absolute;
        width:1px;
        height:1px;
        margin:-1px;
        overflow:hidden;
        clip-path:inset(50%);
        white-space:nowrap;
    }

    .ig-actions{
        display:flex;
        flex-wrap:wrap;
        align-items:center;
        gap:2px;
    }
    /* App.vue's `button{all:unset}` makes IconButton content-box, so its 6px side padding
       lands on top of its 40px and each icon takes 54px. In these rows that cost two
       columns of buttons ~120px; drop it. Touch tablets keep it, as room between their
       buttons' captions (phones drop it too, see the phone rules). */
    @media (hover:hover) and (min-width:601px){
        .ig .icon-btn{ padding:0; }
    }


    /* ---- instructors: one row each ---- */
    .ig-instructors{
        table-layout:fixed;     /* the columns don't move when a row opens */
        background:var(--ig-card);
        border:1px solid var(--ig-line);
        border-radius:12px;
    }
    .ig-instructors th.ig-c-name{ width:25%; padding-left:40px; }
    .ig-instructors th.ig-c-status{ width:7rem; }
    .ig-instructors th.ig-c-groups{ width:5rem; text-align:center; }
    .ig-instructors > thead th:first-child{ border-top-left-radius:11px; }
    .ig-instructors > thead th:last-child{ border-top-right-radius:11px; }
    .ig-instructors > tbody:last-child > tr:last-child > td:first-child{ border-bottom-left-radius:11px; }
    .ig-instructors > tbody:last-child > tr:last-child > td:last-child{ border-bottom-right-radius:11px; }

    .ig-row{ cursor:pointer; }
    .ig-row > td{
        padding:4px 10px;
        border-top:1px solid var(--ig-line-soft);
    }
    .ig-row > td.ig-c-name{ padding-left:6px; }
    .ig-row > td.ig-c-groups{
        text-align:center;
        font-variant-numeric:tabular-nums;
    }
    .ig-row > td.ig-c-actions{ cursor:default; }
    .ig-c-actions > .ig-actions{ justify-content:flex-end; }

    tbody:nth-of-type(even) > .ig-row > td{ background:var(--ig-stripe); }
    tbody > .ig-row:hover > td{ background:var(--ig-row-hover); }
    tbody.ig-open > .ig-row > td{ background:var(--ig-row-open); }
    tbody.ig-open > tr > td:first-child{ box-shadow:inset 3px 0 0 var(--ig-accent); }

    .ig-expand{
        display:flex;
        align-items:center;
        gap:6px;
        width:100%;
        min-width:0;
        min-height:40px;
        padding:4px 8px 4px 4px;
        word-break:normal;
        color:var(--ig-text);
        background:transparent;
        border:none;
        border-radius:8px;
        box-shadow:none;
    }
    .ig-expand:hover{
        color:var(--ig-text);
        background:transparent;
        box-shadow:none;
    }
    .ig-chevron{
        flex:none;
        color:var(--ig-muted);
        transition:transform 0.2s ease;
    }
    .ig-open .ig-chevron{ transform:rotate(90deg); }
    .ig-chevron.loading{ animation:ig-spin 0.9s linear infinite; }
    .ig-name{
        min-width:0;
        font-size:15px;
        font-weight:600;
        overflow:hidden;
        text-overflow:ellipsis;
        white-space:nowrap;
    }
    .ig-off .ig-name{
        font-weight:500;
        color:var(--ig-muted);
    }

    .ig-status{
        display:inline-flex;
        align-items:center;
        gap:7px;
        white-space:nowrap;
    }
    .ig-status::before{
        content:'';
        flex:none;
        width:8px;
        height:8px;
        border-radius:50%;
        background:var(--ig-on);
    }
    .ig-status.off{ color:var(--ig-muted); }
    .ig-status.off::before{ background:var(--ig-off); }

    .ig-unit{ display:none; }


    /* ---- an open instructor: its groups, right under its row ---- */
    .ig-detail > td{
        padding:2px 14px 16px 18px;
        background:var(--ig-row-open);
    }
    .ig-groups{
        display:flex;
        flex-direction:column;
        gap:10px;
        animation:ig-open 0.2s ease-out;
    }
    @keyframes ig-open{
        from{ opacity:0; transform:translateY(-4px); }
        to{ opacity:1; transform:none; }
    }
    @keyframes ig-spin{
        to{ transform:rotate(360deg); }
    }
    .ig-groups-toolbar{
        display:flex;
        flex-wrap:wrap;
        gap:8px;
    }

    .ig-gtable{
        --ig-muted:#5f6269;     /* darker grey: stays readable (AA) on the tinted rows */
        background:var(--ig-card);
        border:1px solid var(--ig-line);
        border-radius:10px;
    }
    .ig-gtable > thead th:first-child{ border-top-left-radius:9px; }
    .ig-gtable > thead th:last-child{ border-top-right-radius:9px; }
    .ig-gtable > tbody:last-child > .ig-lead > td:first-child{ border-bottom-left-radius:9px; }
    .ig-gtable > tbody:last-child > tr:last-child > td:last-child{ border-bottom-right-radius:9px; }
    .ig-gtable th:first-child{ padding-left:14px; }
    .ig-gtable th.ig-g-hours{ text-align:center; }

    /* Text cells are padded so their first line sits level with the 40px buttons. Text wraps
       between words (a subject or note anywhere), the button groups never do; below the
       width where that still fits, groups stack instead (further down). */
    .ig-gtable td{
        padding:14px 8px;
        vertical-align:top;
        overflow-wrap:break-word;
    }
    .ig-gtable td.ig-g-name{ padding-left:14px; }
    .ig-gtable td.ig-g-actions,
    .ig-gtable td.ig-s-actions{ padding:4px 6px; }
    .ig-gtable .ig-actions{ flex-wrap:nowrap; }

    /* Groups alternate blue and sand. A group's own cells carry its colour, with a strip down
       its left edge; its students' rows alternate a light and a darker shade of that same
       colour, so each student is its own row inside its group. White lines part the groups,
       and a group from its students. */
    .ig-group:nth-of-type(odd){
        --band:#cfe3f2;
        --band-s1:#e7f1f9;
        --band-s2:#d9e9f5;
        --band-edge:var(--ig-accent);
    }
    .ig-group:nth-of-type(even){
        --band:#ecdcc4;
        --band-s1:#f8f1e7;
        --band-s2:#f0e5d5;
        --band-edge:#b07d38;
    }
    .ig-gtable td.ig-g{ background-color:var(--band); }
    .ig-gtable td.ig-g-name{ box-shadow:inset 4px 0 0 var(--band-edge); }
    .ig-group > tr:nth-child(odd) > td.ig-s{ background-color:var(--band-s1); }
    .ig-group > tr:nth-child(even) > td.ig-s{ background-color:var(--band-s2); }
    .ig-lead > td{ border-top:2px solid var(--ig-card); }
    .ig-gtable .ig-s-name,
    .ig-gtable .ig-s-none{ border-left:2px solid var(--ig-card); }
    /* the student row under the pointer (a veil, so it keeps its own shade underneath) */
    .ig-group > tr:has(td.ig-s:hover) > td.ig-s{
        background-image:linear-gradient(var(--ig-veil),var(--ig-veil));
    }

    .ig-subject{
        display:block;
        font-weight:600;
        overflow-wrap:anywhere;
    }
    .ig-subject.none{
        font-weight:400;
        font-style:italic;
        color:var(--ig-muted);
    }
    .ig-comments{
        display:block;
        max-width:13rem;        /* a note wraps rather than widening the Group column */
        margin-top:2px;
        font-size:13px;
        line-height:18px;
        color:var(--ig-muted);
        overflow-wrap:anywhere;
    }
    .ig-schedule-entry{
        display:block;
        font-variant-numeric:tabular-nums;
    }
    .ig-gtable td.ig-g-hours{
        text-align:center;
        white-space:nowrap;
        font-variant-numeric:tabular-nums;
    }
    .ig-s-name .material-symbols-outlined{ display:none; }
    .ig-none,
    .ig-s-level.none{ color:var(--ig-muted); }
    .ig-s-none{
        font-style:italic;
        color:var(--ig-muted);
    }

    .ig-empty{
        margin:0;
        color:var(--ig-muted);
        font-style:italic;
    }


    /* ---- touch screens: IconButton is 52px tall there, so the text sits lower ---- */
    @media (hover:none){
        .ig-gtable td{ padding:20px 8px; }
    }


    /* ---- narrower screens and touch tablets: each group stacks into a few lines (group,
       schedule + hours, its buttons, then a line per student), still one under another ---- */
    @media (max-width:1150px), (hover:none) and (max-width:1399px){
        .ig-gtable,
        .ig-gtable > tbody,
        .ig-gtable > tbody > tr,
        .ig-gtable > tbody > tr > td{
            display:block;
        }
        .ig-gtable > thead{ display:none; }

        /* a group is one block in its colour, with its students' lines set inside it, in
           lighter shades than in the table so they stand out from the block */
        .ig-group:nth-of-type(odd){
            --band-s1:#f3f8fc;
            --band-s2:#e8f1f8;
        }
        .ig-group:nth-of-type(even){
            --band-s1:#fcf8f2;
            --band-s2:#f5ede1;
        }
        .ig-gtable > tbody{
            padding:10px 12px 12px 16px;
            background:var(--band);
            box-shadow:inset 4px 0 0 var(--band-edge);
        }
        .ig-gtable > tbody + tbody{ border-top:2px solid var(--ig-card); }
        .ig-gtable > tbody:first-of-type{
            border-top-left-radius:9px;
            border-top-right-radius:9px;
        }
        .ig-gtable > tbody:last-child{
            border-bottom-left-radius:9px;
            border-bottom-right-radius:9px;
        }
        .ig-gtable > tbody > tr{
            display:flex;
            flex-wrap:wrap;
            align-items:stretch;
        }

        /* the table's cell padding, lines and edge strip don't apply to stacked rows */
        .ig-gtable td,
        .ig-gtable td.ig-g-name,
        .ig-gtable td.ig-g-actions,
        .ig-gtable td.ig-s-actions{ padding:0; }
        .ig-lead > td,
        .ig-gtable .ig-s-name,
        .ig-gtable .ig-s-none{ border:none; }
        .ig-gtable .ig-actions{ flex-wrap:wrap; }

        .ig-gtable td.ig-g-name{
            flex:1 1 100%;
            padding-bottom:6px;
            box-shadow:none;
        }
        .ig-gtable td.ig-g-schedule,
        .ig-gtable td.ig-g-hours{
            padding-right:24px;
            text-align:left;
        }
        .ig-g-schedule::before,
        .ig-g-hours::before{
            content:attr(data-label);
            display:block;
            font-size:11px;
            font-weight:600;
            line-height:16px;
            letter-spacing:0.06em;
            text-transform:uppercase;
            color:var(--ig-muted);
        }
        .ig-gtable td.ig-g-actions{
            flex:1 1 100%;
            padding:6px 0 10px;
        }

        /* a student is one line in its shade: name, level, buttons (no gaps, so the shade
           runs the full width) */
        .ig-gtable td.ig-s{
            display:flex;
            align-items:center;
            padding:6px 10px;
        }
        .ig-gtable td.ig-s-name{
            flex:0 1 auto;
            gap:6px;
            min-width:0;
            font-weight:500;
        }
        .ig-gtable td.ig-s-level{
            flex:1 1 auto;
            padding-left:0;
        }
        .ig-gtable td.ig-s-actions{
            flex:none;
            padding:2px 4px;
        }
        /* fills the cell, so on phones (cell full width) the buttons spread like the others */
        .ig-gtable td.ig-s-actions > .ig-actions{ flex:1 1 auto; }
        .ig-gtable td.ig-s-none{ flex:1 1 100%; }
        .ig-s-name .material-symbols-outlined{
            display:inline-block;
            font-size:18px;
            color:var(--ig-muted);
            font-variation-settings:'FILL' 0,'wght' 400,'GRAD' 0,'opsz' 20;
        }
        .ig-level{
            padding:0 8px;
            font-size:12px;
            color:var(--ig-accent-dark);
            background:var(--ig-hover);
            border-radius:999px;
        }
        .ig-s-level.none > .ig-level{ display:none; }
    }


    /* ---- phones and narrow windows: an instructor's row becomes two lines, the name
       (with status and group count) and then its buttons ---- */
    @media (max-width:760px), (hover:none) and (max-width:860px){
        .ig-instructors,
        .ig-instructors > tbody,
        .ig-instructors > tbody > tr,
        .ig-instructors > tbody > tr > td{
            display:block;
        }
        .ig-instructors > thead{ display:none; }
        .ig-instructors > tbody + tbody{ border-top:1px solid var(--ig-line-soft); }
        .ig-instructors > tbody:first-of-type > tr:first-child{
            border-top-left-radius:11px;
            border-top-right-radius:11px;
        }
        .ig-instructors > tbody:last-child > tr:last-child{
            border-bottom-left-radius:11px;
            border-bottom-right-radius:11px;
        }
        .ig-instructors > tbody > tr.ig-row{
            display:flex;
            flex-wrap:wrap;
            align-items:center;
            column-gap:10px;
            padding:4px 8px 6px 4px;
        }

        /* stripes, hover and the open marker move from the cells to the row */
        .ig-row > td,
        .ig-row > td.ig-c-name{
            padding:0;
            border:none;
        }
        tbody:nth-of-type(even) > .ig-row > td,
        tbody > .ig-row:hover > td,
        tbody.ig-open > .ig-row > td{ background:none; }
        tbody.ig-open > .ig-row > td:first-child{ box-shadow:none; }
        tbody:nth-of-type(even) > .ig-row{ background:var(--ig-stripe); }
        tbody > .ig-row:hover{ background:var(--ig-row-hover); }
        tbody.ig-open > .ig-row{
            background:var(--ig-row-open);
            box-shadow:inset 3px 0 0 var(--ig-accent);
        }

        .ig-row > .ig-c-name{
            flex:1 1 0;
            min-width:0;
        }
        .ig-row > .ig-c-status,
        .ig-row > .ig-c-groups{
            flex:none;
            font-size:13px;
        }
        .ig-unit{ display:inline; }
        .ig-row > .ig-c-actions{ flex:1 1 100%; }
        .ig-c-actions > .ig-actions{ justify-content:flex-start; }
        .ig-detail > td{ padding:2px 8px 10px 12px; }
    }


    /* ---- phones ---- */
    @media (max-width:600px){
        .ig{
            padding:8px;
            border-radius:0;
        }
        .ig-row > .ig-c-actions > .ig-actions,
        .ig-gtable .ig-actions{ justify-content:space-between; }
        /* space-between already spaces the buttons out; without IconButton's side padding a
           row of five or six fits a phone's width instead of wrapping one button */
        .ig .icon-btn{ padding:0; }
        .ig-gtable td.ig-s-actions{
            flex:1 1 100%;
            padding:0 4px 4px;
        }
        .ig-gtable > tbody{ padding:10px 8px 10px 12px; }
        .ig-detail > td{ padding:2px 6px 8px 8px; }
        .ig-groups-toolbar .ig-add{
            flex:1 1 auto;
            justify-content:center;
        }
    }

    @media (prefers-reduced-motion:reduce){
        .ig-groups,
        .ig-chevron.loading{ animation:none; }
        .ig-chevron,
        .ig-title-chevron{ transition:none; }
    }

</style>
